import type { Dictionary } from "./types";

export const en: Dictionary = {
  nav: {
    navigation: "Navigation",
    dashboard: "Dashboard",
    devices: "Devices",
    map: "Map",
    settings: "Settings",
    openMenu: "Open menu",
    closeMenu: "Close menu",
    admin: "Admin",
    sites: "Sites",
    users: "Users",
  },
  dashboard: {
    title: "Dashboard",
    updatedAt: "Updated at",
    online: "Online",
    offline: "Offline",
    warning: "Warning",
    total: "Total",
    batteryStatus: "Battery Status",
    batteryDistribution: "Battery level distribution",
    batteryHigh: "High (>=60%)",
    batteryMedium: "Medium (20-59%)",
    batteryLow: "Low (<20%)",
    inactivity: "Device Inactivity",
    inactivitySubtitle: "Animals with no detected movement",
    criticalCases: "Critical cases",
    allDevicesActive: "All devices are active",
    gatewayTitle: "LoRaWAN Gateways",
    gatewayOfOnline: "of",
    pktToday: "pkt today",
    devicesConnected: "devices",
  },
  devicesPage: {
    title: "Devices",
    trackersConfigured: "GPS trackers configured",
    searchPlaceholder: "Search by name or serial...",
    allStatuses: "All statuses",
    tags: "Tags",
    removeAll: "Remove all",
    devicesFound: "devices found",
    deviceFound: "device found",
    name: "Name",
    serial: "Serial",
    status: "Status",
    battery: "Battery",
    lastSignal: "Last signal",
    tagsColumn: "Tags",
    config: "Config",
    site: "Site",
    noSiteAssigned: "No site assigned",
    selectSite: "Select site...",
    noSitesAvailable: "No sites available",
    assignSite: "Assign to site",
    noDevicesFound: "No devices found with the applied filters.",
    deviceInfo: "Device info",
    firmware: "Firmware",
    dataFrequency: "Data frequency",
    species: "Species",
    gateway: "Gateway",
    actions: "Actions",
    firmwareUpdate: "Firmware update",
    changeDataFrequency: "Change data frequency",
    sendConfiguration: "Send configuration",
    deviceConfiguration: "Device configuration",
    removeTag: "Remove tag",
    loading: "Loading devices...",
    errorLoading: "Error loading",
    batteryHistory: "Battery history",
    noBatteryData: "No battery data available",
  },
  mapPage: {
    mapFilters: "Map Filters",
    closePanel: "Close panel",
    device: "Device",
    searchDevice: "Search device...",
    allDevices: "All devices",
    positionHistory: "Position History",
    showHistoricTrack: "Show the historic track of the selected device",
    dateRange: "Date Range",
    from: "From",
    to: "To",
    clearDateFilter: "Clear date filter",
    selectedDevice: "Selected device",
    lat: "Lat",
    lng: "Lng",
    battery: "Battery",
    showAll: "Show all",
    filters: "Filters",
    loadingMap: "Loading map...",
    speed: "Speed",
    time: "Time",
    pointOf: "Point",
    downloadCsv: "Download CSV",
    showPolygonOverlay: "Site Area",
    showPolygonOverlayHint: "Show/hide the site polygon on the map",
    showGateways: "Gateways",
    showGatewaysHint: "Show/hide gateway antennas on the map",
    baseMapType: "Map Type",
    baseMapStandard: "Standard",
    baseMapSatellite: "Satellite",
    baseMapTerrain: "Elevation",
  },
  settingsPage: {
    title: "Settings",
    subtitle: "Application preferences",
    language: "Language",
    languageDescription: "Choose the interface language",
    english: "English",
    italian: "Italiano",
  },
  time: {
    now: "Now",
    minAgo: "min ago",
    hoursAgo: "h ago",
    daysAgo: "d ago",
  },
  auth: {
    title: "Portal Login",
    subtitle: "Login to view GPS tracker data",
    username: "Username",
    password: "Password",
    login: "Login",
    error: "Authentication error",
    invalidCredentials: "Invalid credentials",
  },
  sitesPage: {
    title: "Sites",
    subtitle: "Manage your tracker locations and areas",
    addSite: "Add Site",
    editSite: "Edit Site",
    newSite: "New Site",
    name: "Name",
    description: "Description",
    gpsLocation: "GPS Location",
    referenceGateway: "Reference Gateway",
    selectGateway: "Select a gateway...",
    latitude: "Latitude",
    longitude: "Longitude",
    sampleLat: "e.g. 45.4642",
    sampleLng: "e.g. 9.1900",
    areaPolygon: "Area (GPS Polygon) - Optional",
    polygonHint: "Select points on the map or use text values. Minimum 3 points to define an area.",
    save: "Save",
    cancel: "Cancel",
    sitesList: "Sites List",
    loading: "Loading...",
    noSitesFound: "No sites found.",
    headers: {
      name: "Name",
      description: "Description",
      coordinates: "Coordinates",
      polygonArea: "Polygon Area",
      actions: "Actions",
    },
    noData: {
      lat: "-",
      lng: "-",
    },
    alerts: {
      deleteConfirm: "Are you sure you want to delete this site?",
      siteCreatedError: "Error creating site",
      siteUpdatedError: "Error updating site",
      siteDeletedError: "Error deleting site",
    },
    // Polygon editor
    polygonPointsTitle: "Polygon Points",
    polygonPoint: "Point",
    removePoint: "Remove point",
    polygonValid: "Valid polygon (minimum 3 points)",
    pointsNeeded: "Still needed",
    pointsForPolygon: "points to form a polygon",
  },
  common: {
    createSecurePassword: "Create a secure password...",
    repeatPassword: "Repeat the password...",
    centeredOnLocation: "Centered on GPS location. Click to add points to the polygon.",
  },
  usersPage: {
    title: "Users",
    subtitle: "Manage portal users",
    addUser: "Add User",
    editUser: "Edit User",
    newUser: "New User",
    username: "Username",
    fullName: "Full Name",
    password: "Password",
    confirmPassword: "Confirm Password",
    description: "Description",
    siteAccess: "Site Access",
    selectSites: "Select sites the user can access",
    save: "Save",
    cancel: "Cancel",
    usersList: "Users List",
    loading: "Loading...",
    noUsersFound: "No users found.",
    headers: {
      username: "Username",
      name: "Name",
      description: "Description",
      authorizedSites: "Authorized Sites",
      actions: "Actions",
    },
    alerts: {
      passwordRequired: "Password is required for a new user",
      passwordsMismatch: "Passwords do not match",
      usernameRequired: "Username is required",
      userCreatedError: "Error creating user",
      userUpdatedError: "Error updating user",
      userDeletedError: "Error deleting user",
      deleteConfirm: "Are you sure you want to delete this user?",
      siteAccessTitle: "Sites Access",
    },
  },
};
